import { c as _c } from "react/compiler-runtime";
import { TabsHeader } from 'platform/components';
import { useState } from 'react';
import { isNotNil } from 'ramda';
import { useColumnMenuContext } from '../../../../../../context/ColumnMenuContext';
import type { EagColumn } from '../../../../../../types/EagColumn';
import { ColumnMenuFilterTab } from './components/ColumnMenuFilterTab/ColumnMenuFilterTab';
import { ColumnMenuOptionsTab } from './components/ColumnMenuOptionsTab/ColumnMenuOptionsTab';
type ActiveTab = 'filter' | 'options';
type ColumnHeaderMenuProps = {
  column: EagColumn;
  columnPinned: 'LEFT' | 'RIGHT' | null | undefined;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Column header dropdown menu content with two icon tabs (filter + options).
 *
 * ## Tabs — Google-style
 * - Filter tab (funnel icon): renders the column's filter widget inline
 * - Options tab (menu icon): pin / autosize / reset actions
 * - Active tab has a blue underline indicator
 * - When the column has no filter definition, only the options tab is shown (no tab bar)
 *
 * ## Positioning
 * - Rendered as children of <Dropdown> from platform/components (in HeaderCell).
 * - Dropdown handles portal, positioning, focus management and dismiss.
 *
 *
 * ## State / callbacks
 * - Filter state and all action callbacks are read from ColumnMenuContext (no prop drilling)
 */
export function ColumnHeaderMenu(props) {
  const $ = _c(24);
  const columnMenu = useColumnMenuContext();
  let t0;
  if ($[0] !== props.column.filter) {
    t0 = isNotNil(props.column.filter);
    $[0] = props.column.filter;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasFilter = t0;
  const t1 = columnMenu.filterState[props.column.key];
  let t2;
  if ($[2] !== t1) {
    t2 = isNotNil(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasActiveFilter = t2;
  const [activeTab, setActiveTab] = useState(!hasFilter ? "options" : "filter");
  let t3;
  if ($[4] !== activeTab || $[5] !== hasActiveFilter || $[6] !== hasFilter) {
    t3 = hasFilter && <TabsHeader variant="container" tabs={[{
      id: "filter",
      title: "Filter",
      icon: hasActiveFilter ? "default" : undefined
    }, {
      id: "options",
      title: "Options"
    }]} activeTabId={activeTab} onChange={id => setActiveTab(id as ActiveTab)} isFullWidth={true} />;
    $[4] = activeTab;
    $[5] = hasActiveFilter;
    $[6] = hasFilter;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== activeTab || $[9] !== columnMenu.filterState || $[10] !== columnMenu.onFilterChange || $[11] !== hasFilter || $[12] !== props.column) {
    t4 = activeTab === "filter" && hasFilter && <ColumnMenuFilterTab column={props.column} filterState={columnMenu.filterState} onFilterChange={columnMenu.onFilterChange} />;
    $[8] = activeTab;
    $[9] = columnMenu.filterState;
    $[10] = columnMenu.onFilterChange;
    $[11] = hasFilter;
    $[12] = props.column;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== activeTab || $[15] !== props.column.key || $[16] !== props.column.sortable || $[17] !== props.columnPinned || $[18] !== props.onClose) {
    t5 = activeTab === "options" && <ColumnMenuOptionsTab currentPinned={props.columnPinned} columnKey={props.column.key} columnSortable={props.column.sortable === true} onClose={props.onClose} />;
    $[14] = activeTab;
    $[15] = props.column.key;
    $[16] = props.column.sortable;
    $[17] = props.columnPinned;
    $[18] = props.onClose;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="min-w-[360px] max-w-[400px]">{t3}{t4}{t5}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}